import { useCallback, useEffect, useState } from 'react';
import { countComments, sortStream, type CommentAnchor, type CommentStreamEntry } from './commentsModel';
import { getCommentsService } from './commentsService';

export interface UseCommentsResult {
  entries: CommentStreamEntry[];
  /** Number of user comments (system events are not counted). */
  count: number;
  loadError: boolean;
  post: (input: { author: string; text: string; anchor: CommentAnchor }) => Promise<void>;
}

/**
 * Comment stream for one job, loan or search. Pass `undefined` to stay idle
 * (flag off, or a draft item that has no id yet).
 */
export function useComments(itemId: string | undefined): UseCommentsResult {
  const [entries, setEntries] = useState<CommentStreamEntry[]>([]);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    setEntries([]);
    setLoadError(false);
    if (!itemId) return undefined;

    const service = getCommentsService();
    let active = true;
    const load = () => {
      service
        .list(itemId)
        .then((list) => {
          if (!active) return;
          setEntries(sortStream(list));
          setLoadError(false);
        })
        .catch(() => {
          if (active) setLoadError(true);
        });
    };
    load();
    const unsubscribe = service.subscribe(itemId, load);
    return () => {
      active = false;
      unsubscribe();
    };
  }, [itemId]);

  const post = useCallback(
    async (input: { author: string; text: string; anchor: CommentAnchor }) => {
      if (!itemId) return;
      await getCommentsService().add(itemId, input);
    },
    [itemId]
  );

  return { entries, count: countComments(entries), loadError, post };
}
